import { useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Menu, X, ArrowUpRight, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const LINKS = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/services", label: "Services" },
  { to: "/industries", label: "Industries" },
  { to: "/careers", label: "Careers" },
  { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    setOpen(false);
    navigate("/");
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/80 shadow-[0_2px_12px_rgba(0,0,0,0.03)]" data-testid="navbar">
      <nav className="max-w-7xl mx-auto px-6 h-[72px] flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" className="font-display font-semibold text-xl tracking-tight text-slate-900">
          Samanvay<span className="text-brand"> HR</span>
        </Link>

        <div className="hidden lg:flex items-center gap-8">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${isActive ? "text-brand" : "text-slate-500 hover:text-slate-900"}`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-3">
          {user ? (
            <>
              <Link
                to="/dashboard"
                data-testid="nav-dashboard-btn"
                className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200 px-4 py-2"
              >
                Dashboard
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout-btn"
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-brand transition-colors duration-200"
              >
                <LogOut size={15} /> Sign out
              </button>
            </>
          ) : (
            <Link
              to="/login"
              data-testid="nav-signin-btn"
              className="text-sm text-slate-600 hover:text-slate-900 transition-colors duration-200 px-4 py-2"
            >
              Sign in
            </Link>
          )}
          <Link
            to="/contact"
            data-testid="nav-hire-talent-btn"
            className="group flex items-center gap-2 rounded-full bg-brand px-5 py-2.5 text-sm font-medium text-white transition-all duration-300 hover:bg-[#E63E12] hover:shadow-glow"
          >
            Hire Talent
            <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          className="lg:hidden text-slate-900"
          onClick={() => setOpen(!open)}
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {open && (
        <div className="lg:hidden border-t border-slate-200 bg-white/95 backdrop-blur-2xl px-6 py-6 space-y-4 shadow-xl" data-testid="nav-mobile-menu">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              onClick={() => setOpen(false)}
              data-testid={`nav-mobile-link-${l.label.toLowerCase()}`}
              className="block text-base text-slate-600 hover:text-brand transition-colors duration-200"
            >
              {l.label}
            </NavLink>
          ))}
          <div className="pt-4 border-t border-slate-100 flex items-center gap-4">
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="nav-mobile-dashboard-btn" className="text-brand text-sm">
                  Dashboard
                </Link>
                <button onClick={handleLogout} data-testid="nav-mobile-logout-btn" className="text-sm text-slate-400">
                  Sign out
                </button>
              </>
            ) : (
              <Link to="/login" onClick={() => setOpen(false)} data-testid="nav-mobile-signin-btn" className="text-sm text-slate-600">
                Sign in
              </Link>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
